import React from 'react';

import { useAssertCurrentOrganizationId } from '../../state';
import {
    extractActiveAgentTask,
    type GitBookAgentClientState,
    useAgentState,
    useChangeRequestAgent,
} from '../Agent';
import { GitBookAgentAvatar } from '../Users';

/**
 * Display an indicator when the agent is responding to a comment.
 */
export function CommentAgentIndicator(props: {
    /* The space that the comment is in. */
    spaceId: string;
    /* The change request that the comment is in. */
    changeRequestId: string;
    /** ID of the comment to display the indicator for. */
    commentId: string;
}) {
    const { spaceId, changeRequestId, commentId } = props;

    const organizationId = useAssertCurrentOrganizationId();

    const agent = useChangeRequestAgent({
        organizationId,
        spaceId,
        changeRequestId,
    });

    const isAgentResponding = useAgentState(
        agent,
        React.useCallback(
            (state: GitBookAgentClientState) => {
                const task = extractActiveAgentTask(state);

                // Check that the agent is tasked with responding to the comment
                if (!task || task.type !== 'respond_to_comment' || task.commentId !== commentId) {
                    return false;
                }

                // Check that the agent hasn't posted the reply yet
                const lastMessage = state.messages[state.messages.length - 1];
                const hasReplyTool = lastMessage?.steps.some((step) =>
                    step.toolCalls?.some((toolCall) => toolCall.tool === 'replyToComment')
                );

                return !hasReplyTool;
            },
            [commentId]
        )
    );

    if (!isAgentResponding) {
        return null;
    }

    return (
        <div className="flex items-center gap-4">
            <GitBookAgentAvatar size="sm" loading />
            <div className="animate-pulse text-muted text-ui-small">Thinking…</div>
        </div>
    );
}
